import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import articlesData from './articlesData';
import TopSection from "./TopSection";
import { Helmet } from 'react-helmet-async';

const Article = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const { id } = useParams();
  const article = articlesData.find(article => article.id === parseInt(id));

  if (!article) {
    return <div>Article not found</div>;
  }

  const siteUrl = 'https://globalconstructioncup.com';

  const articleText = article.content
    ?.filter(block => block.type === 'paragraph' || block.type === 'header' || block.type === 'sub-header')
    ?.map(block => block.text)
    ?.join(' ') || '';

  const isRanoKhamrakulArticle =
    article.title.includes('Рано Хамракул') ||
    articleText.includes('Рано Хамракул') ||
    articleText.includes('Rano Khamrakul');

  const canonicalUrl = `${siteUrl}/articles/${article.id}`;

  const seoTitle = isRanoKhamrakulArticle
    ? 'Рано Хамракул (Rano Khamrakul) — победитель Global Construction Cup 2021'
    : `${article.title} | Global Construction Cup`;

  const seoDescription = isRanoKhamrakulArticle
    ? 'Рано Хамракул (Rano Khamrakul) — победитель Global Construction Cup 2021 в номинации «Лучший руководитель строительной компании» за внедрение инновационных решений в реконструкции жилых объектов и успешное управление проектами в сфере ремонта и дизайна.'
    : articleText.substring(0, 160);

  const seoKeywords = isRanoKhamrakulArticle
    ? 'Рано Хамракул, Рано Хамракул, Rano Khamrakul, Rano Khamrakul Kazakhstan, Rano Khamrakul Global Construction Cup, Рано Хамракул Казахстан, Global Construction Cup 2021, Лучший руководитель строительной компании, строительная компания, реконструкция жилых объектов, ремонт, дизайн, construction executive, construction company executive, renovation, interior design, residential reconstruction'
    : `${article.title}, Global Construction Cup, новости строительства, строительная отрасль`;

  const imageUrl = article.image
    ? article.image.startsWith('http')
      ? article.image
      : `${siteUrl}${article.image}`
    : `${siteUrl}/logo.png`;

  const schemaData = isRanoKhamrakulArticle
    ? {
        "@context": "https://schema.org",
        "@type": "Article",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl
        },
        "headline": "Итоги Global Construction Cup: Рано Хамракул — победитель в номинации «Лучший руководитель строительной компании»",
        "alternativeHeadline": "Rano Khamrakul — Winner of Global Construction Cup 2021",
        "description": seoDescription,
        "image": imageUrl,
        "datePublished": "2021-06-25",
        "dateModified": "2021-06-25",
        "author": {
          "@type": "Person",
          "name": article.autor
        },
        "publisher": {
          "@type": "Organization",
          "name": "Global Construction Cup",
          "url": siteUrl
        },
        "about": {
          "@type": "Person",
          "name": "Rano Khamrakul",
          "alternateName": "Рано Хамракул",
          "award": "Global Construction Cup 2021 — Лучший руководитель строительной компании",
          "description": "Рано Хамракул (Rano Khamrakul) — победитель Global Construction Cup 2021 в номинации «Лучший руководитель строительной компании» за внедрение инновационных решений в реконструкции жилых объектов и успешное управление проектами в сфере ремонта и дизайна.",
          "knowsAbout": [
            "construction management",
            "renovation",
            "interior design",
            "residential reconstruction",
            "project management"
          ]
        },
        "keywords": seoKeywords,
        "articleBody": articleText
      }
    : {
        "@context": "https://schema.org",
        "@type": "Article",
        "mainEntityOfPage": {
          "@type": "WebPage",
          "@id": canonicalUrl
        },
        "headline": article.title,
        "description": seoDescription,
        "image": imageUrl,
        "author": {
          "@type": "Person",
          "name": article.autor
        },
        "publisher": {
          "@type": "Organization",
          "name": "Global Construction Cup",
          "url": siteUrl
        },
        "keywords": seoKeywords,
        "articleBody": articleText
      };

  const renderContent = (content) => {
    return content.map((block, index) => {
      switch (block.type) {
        case 'paragraph':
          return <p key={index} className="article-paragraph">{block.text}</p>;
        case 'header':
          return <h5 key={index} className="article-header">{block.text}</h5>;
        case 'sub-header':
          return <h6 key={index} className="article-sub-header">{block.text}</h6>;
        case 'image':
          return <img key={index} src={block.src} alt={block.alt} className="article-page-image" />;
        case 'list':
          return (
            <ul className='position-list' key={index}>
              <li>{block.text}</li>
            </ul>
          );
        default:
          return null;
      }
    });
  };

  return ( 
    <>
      <Helmet>
        <html lang="ru" />

        <title>{seoTitle}</title>

        <meta name="description" content={seoDescription} />
        <meta name="keywords" content={seoKeywords} />

        <link rel="canonical" href={canonicalUrl} />

        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="Global Construction Cup" />
        <meta property="og:locale" content="ru_RU" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoTitle} />
        <meta name="twitter:description" content={seoDescription} />
        <meta name="twitter:image" content={imageUrl} />

        {isRanoKhamrakulArticle && (
          <>
            <meta name="author" content="Семен Синельников" />
            <meta name="subject" content="Рано Хамракул (Rano Khamrakul)" />
            <meta name="robots" content="index, follow" />
          </>
        )}

        <script type="application/ld+json">
          {JSON.stringify(schemaData)}
        </script>
      </Helmet>

      <TopSection
        subtitle="Новости"
        title="Global Construction Cup"
      />

      <div className="article-container">
        <div className="article-page">
          <h1 className="article-page-title">{article.title}</h1>
          <div className="article-meta">
            <span className="article-date">
              <i className="bi bi-calendar icon"></i>{article.date}
            </span>
          </div>
          <img src={article.image} alt={article.title} className="article-page-image" />
          <div className="article-page-content">
            {renderContent(article.content)}
          </div>
          <div className="article-page-autor">Автор: {article.autor}</div>
        </div>
      </div>
    </>
  );
};

export default Article;